import { useQuery } from "@tanstack/react-query";
import { buildHref } from "@softmax/web-addresses";
import { useActualViewer } from "@softmax/web-features/session/records";
import { NavigationReady } from "@softmax/web-features/navigation/markers";
import { PlayLocalTime, PlayRequestsView } from "@softmax/product-ux/play";
import { formatCredits, formatInt } from "@softmax/product-ux/play-values";
import { useGameContext } from "@/features/game/state";
import { activityTimeZone } from "./lib/activity/feed-calendar";
import { groupRequestsByDay } from "./lib/activity/requests-index";
import { playRequestsOptions } from "./play.queries";
import { PlayResource } from "./play-resource";
export function PlayRequestsPage() {
  const game = useGameContext();
  const viewer = useActualViewer();
  const query = useQuery({
    ...playRequestsOptions({
      href: game.publicPath,
      viewerId: viewer.data?.subject_id ?? null,
    }),
    enabled: viewer.isSuccess,
  });
  return (
    <PlayResource query={query} requiresSession>
      {(data) => {
        const stats: {
          readonly label: string;
          readonly value: string;
        }[] = [];

        if (data.credits)
          stats.push(
            {
              label: "Balance",
              value: `${formatCredits(data.credits.balance)} left`,
            },
            {
              label: "Spent",
              value: `${formatCredits(data.credits.spentThisPeriod)} in this window`,
            },
          );

        stats.push({
          label: "You per day",
          value: formatInt(data.requests.perDayInWindow),
        });
        const groups = groupRequestsByDay({
          rows: data.requests.recent,
          now: data.generatedAt,
          timeZone: activityTimeZone,
        });
        const shown = data.requests.recent.length;
        return (
          <>
            <NavigationReady stage="content" path={game.publicPath} />
            <PlayRequestsView
              leagueName={data.league.name}
              playHref={buildHref({ name: "game.play", ...data.league.route })}
              stats={stats}
              creditsAvailable={data.credits !== null}
              groups={groups.map((group) => ({
                heading: group.heading,
                rows: group.rows.map((row) => ({
                  id: row.id,
                  timestamp: (
                    <PlayLocalTime
                      iso={row.createdAt}
                      now={data.generatedAt}
                      dayOnly
                    />
                  ),
                  description: `${formatInt(row.episodeCount)} ep · ${row.coworldName ?? "—"}`,
                  cost:
                    row.costCredits === null
                      ? "—"
                      : `${formatCredits(row.costCredits)} credits`,
                })),
              }))}
              countLabel={
                data.requests.totalCount !== null &&
                data.requests.totalCount > shown
                  ? `${shown} of ${formatInt(data.requests.totalCount)}`
                  : `${shown}`
              }
            />
          </>
        );
      }}
    </PlayResource>
  );
}
